<template>
    <v-card light :style="{'margin-top': `${margin}px`}">
        <v-btn
            icon
            absolute
            class="property-close-btn"
            text
            @click="onCloseProperty"
        >
            <v-icon>close</v-icon>
        </v-btn>
        <v-card-title primary-title>
            <h2 class="grey--text lighten-3 text-truncate">{{ property.name }}</h2>
        </v-card-title>
        <v-card-text>
            <v-form :value="formIsValid" ref="form" v-on:submit.prevent>
                <v-text-field
                    ref="nameField"
                    :label="$t('instance.data_model.form_editor.propertie_form.property_name')"
                    v-model="property.name"
                    :rules="formActive?nameRules:[]"
                    :disabled="readOnly"
                    style="position: relative;"
                    autofocus
                    required
                    @input.native="checkForSuggestion"
                ></v-text-field>
                <name-suggestion
                    ref="modal-case-suggestion"
                    class="property-name-suggestion-modal"
                    @suggestion-accepted="property.name=$event"
                ></name-suggestion>
                <v-select
                    v-model="property.datatype"
                    :items="availableFieldsType"
                    :label="$t('instance.data_model.form_editor.propertie_form.property_type')"
                    :rules="formActive?typeRules:[]"
                    :disabled="readOnly"
                    required
                    @change="onDatatypeChange"
                >
                </v-select>
                <v-text-field
                    :label="$t('instance.data_model.form_editor.propertie_form.property_help_text')"
                    v-model="property.attributes.help_text"
                    :disabled="readOnly"
                ></v-text-field>
                <v-checkbox
                    v-model="property.attributes.allow_empty"
                    class="propertie-checkbox"
                    :disabled="readOnly"
                    :label="$t('instance.data_model.form_editor.propertie_form.property_empty_allowed')"
                ></v-checkbox>
                <v-checkbox
                    v-model="property.attributes.unique"
                    class="propertie-checkbox"
                    :disabled="readOnly"
                    :label="$t('instance.data_model.form_editor.propertie_form.property_unique')"
                ></v-checkbox>
                <template v-if="property.datatype">
                    <template v-if="typeIsRel">
                        <v-autocomplete
                            v-model="property.attributes.to"
                            :label="$t('instance.data_model.form_editor.propertie_form.property_select_target')"
                            :disabled="readOnly"
                            :items="availableRelTarget"
                            :rules="formActive?relRule:[]"
                            required
                        >
                        </v-autocomplete>
                        <v-text-field
                            :label="$t('instance.data_model.form_editor.propertie_form.property_reverse')"
                            v-model="property.attributes.reverse"
                            :disabled="readOnly"
                            :rules="formActive?reverseRules:[]"
                            required
                        >
                            <div slot="append">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon small dark v-on="on">info</v-icon>
                                    </template>
                                    <span>
                                        {{$t('instance.data_model.form_editor.propertie_form.info')}}
                                    </span>
                                </v-tooltip>
                            </div>
                        </v-text-field>
                    </template>
                    <template v-else>
                        <template v-if="fieldForDatatype">
                            <component
                                ref="field-type"
                                v-bind:is="fieldForDatatype"
                                v-model="property.attributes.default"
                                :property="property"
                                :readOnly="readOnly"
                                @validate-form="validateForm"
                                @set-ip-protocol="setIpProtocol"
                            ></component>
                        </template>
                    </template>
                    <v-checkbox
                        v-model="representationField"
                        :disabled="readOnly"
                        :label="$t('instance.data_model.form_editor.propertie_form.checkbox_representation')"
                        @change="updateRepresentationField"
                    >
                        <div slot="append">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-icon small v-on="on">info</v-icon>
                                </template>
                                <span>
                                    {{$t('instance.data_model.form_editor.propertie_form.identification_field')}}
                                </span>
                            </v-tooltip>
                        </div>
                    </v-checkbox>

                </template>
            </v-form>
            <transition name="grow">
                <div
                    v-if="!formIsValid && triedSavedWithError"
                    class="red--text text-center"
                    v-html="$t('instance.data_model.form_editor.propertie_form.cant_save')"
                >
                </div>
            </transition>
        </v-card-text>
        <v-card-actions
            class="property-actions-container"
        >
            <v-spacer></v-spacer>
            <v-btn
                v-if="!readOnly"
                :color="!saveBtnError && !modelsHasErrors ? 'info' : ''"
                class="update-buton"
                :class="{'grey lighten-1': saveBtnError || modelsHasErrors}"
                @click="saveModelisation"
            >
                {{$t('instance.data_model.form_editor.propertie_form.save_btn')}}&nbsp;
            </v-btn>
            <v-btn
                v-if="!readOnly"
                color="error"
                @click="onDeleteProperty"
            >
                {{$t('instance.data_model.form_editor.propertie_form.delete_btn')}}
            </v-btn>
        </v-card-actions>

        <dialog-confirm
            ref="modal-delete-field-on-close"
            :text="$t('instance.data_model.form_editor.propertie_form.confirm_cancel_text')"
            :title="$t('instance.data_model.form_editor.propertie_form.confirm_cancel_title')"
            :callback-confirm="closeProperty"
            :text-confirm="$t('instance.data_model.form_editor.propertie_form.confirm_cancel_delete_text')"
            :text-decline="$t('instance.data_model.form_editor.propertie_form.decline_cancel_text')"
        ></dialog-confirm>
        <dialog-confirm
            ref="modal-delete-field"
            :text="$t('instance.data_model.form_editor.propertie_form.confirm_text')"
            :title="$t('instance.data_model.form_editor.propertie_form.confirm_title')"
            :callback-confirm="deleteProperty"
            :text-confirm="$t('instance.data_model.form_editor.propertie_form.confirm_delete_text')"
            :text-decline="$t('instance.data_model.form_editor.propertie_form.decline_text')"
        ></dialog-confirm>
    </v-card>
</template>

<script>
import { isEqual, snakeCase, cloneDeep } from 'lodash';
import { diff } from 'deep-diff';
import { mapGetters } from 'vuex';
import DialogConfirm from '@/components/Partials/dialog-confirm.vue';
import TxtField from './FieldsTypes/txt-field.vue';
import NumberField from './FieldsTypes/number-field.vue';
import TxtAreaField from './FieldsTypes/txt-area-field.vue';
import DateField from './FieldsTypes/date-field.vue';
import FileField from './FieldsTypes/file-field.vue';
import IpField from './FieldsTypes/ip-field.vue';
import NameSuggestion from '../name-suggestion.vue';
import rulesMixins from '@/components/Mixins/rulesMixins.vue';
export default {
    mixins: [rulesMixins],
    components: {
        DialogConfirm,
        NumberField,
        TxtAreaField,
        DateField,
        FileField,
        NameSuggestion,
        IpField,
    },

    props: {
        margin: {
            type: Number,
            required: true,
        },
        property: {
            type: Object,
            required: true,
        },
        schema: {
            type: Object,
            required: true,
        },
        currentModel: {
            type: Object,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        }
    },

    data (){
        return {
            propertyOldValue: null,
            fieldHasError: false,
            // formIsValid: false,
            nameRules: [
                v => this.onInputValid(!!v) || this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.nameRules_required')),

                v => this.onInputValid(v && !this.otherFieldsName.includes(v)) || this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.nameRules_exists')),

                v => {
                    let reservedKeyWords = this.reservedKeyWords.concat(this.reservedFieldNames);
                    if(this.currentModel.name === 'User'){
                        reservedKeyWords = reservedKeyWords.concat(this.reservedUserFieldNames);
                    }
                    if(this.onInputValid(v && !reservedKeyWords.includes(v)) ){
                        return true;
                    }
                    return this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.nameRules_reserved', {v: v}));

                },
                v => this.onInputValid(v && this.invalidNameCase === null) || this.onInputError(''),
            ],
            boolFieldRule: [
                v => this.onInputValid(!v || typeof v === 'boolean' || ['', 'true', 'false'].includes(v.toLowerCase())) ||
                this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.boolFieldRule_value')),
            ],
            relRule: [
                v => this.onInputValid(!!v) ||
                this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.boolFieldRule_target_relation')),
            ],
            reverseRules: [
                v => this.onInputValid(!!v) ||
                 this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.reverseRules_reverse_required')),
                v => this.onInputValid(!/\s/.test(v)) ||
                 this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.reverseRules_reverse_spaces')),
                v => {
                    let fieldWithSameReverseName = this.searchForMatchingReverseName(v);
                    if(fieldWithSameReverseName){
                        return this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.reverseRules_reverse_target_taken', {property: this.property.attributes.reverse}));
                    }
                    this.onInputValid();
                    return !v;
                }
            ],
            typeRules: [
                v => !!v || this.onInputError(this.$t('instance.data_model.form_editor.propertie_form.typeRules_required')),
            ],
            representationField: false,
            triedSavedWithError: false,
            propertyBeforeUpdate: null,
        };
    },

    computed: {
        ...mapGetters([
            'listDatatype',
            'listRelDatatype',
            'reservedKeyWords',
            'reservedFieldNames',
            'reservedUserFieldNames',
            'modelsWithError',
            'modelsHasErrors',
            'fieldInModelHasError'
        ]),
        availableFieldsType (){
            return this.listDatatype;
        },

        typeIsRel (){
            return this.listRelDatatype.find((type) => type === this.property.datatype);
        },

        availableRelTarget (){
            let modelsName = cloneDeep(this.schema.modelList.map((model) => { return {text: model.name, value: {uid: model.uid, name: model.name} };}));
            modelsName.sort((a,b) => {
                if(a.text > b.name) return 1;
                else if(a.text < b.name) return -1;
                else return 0;
            });
            return modelsName;
        },

        otherFieldsName (){
            let fieldsName = this.currentModel.fields.map((f) => f.name);
            fieldsName.splice(fieldsName.indexOf(this.property.name), 1);
            return fieldsName;
        },

        fieldForDatatype (){
            let datatype = this.property.datatype;
            if(['bool', 'char', 'uid', 'email'].includes(datatype)){
                return TxtField;
            }else if(['int', 'bigint', 'float', 'decimal'].includes(datatype)){
                return NumberField;
            }else if(['url', 'txt'].includes(datatype)){
                return TxtAreaField;
            }else if(['date', 'datetime'].includes(datatype)){
                return DateField;
            }else if(['image', 'file', 'json', 'point'].includes(datatype)){
                return FileField;
            }else if(datatype === 'ip'){
                return IpField;
            }else{
                return null;
            }
        },

        invalidNameCase (){
            return this.$helpers.invalidNameCase(this.property.name, 'field');
        },

        saveBtnError (){
            return this.formIsValid === false || this.invalidNameCase !== null;
        },

        canRemoveField (){
            // Field can be removed only if datamodel is not read only abd if there are errors one of them is in this field
            return !this.readOnly && !this.errorsPreventDelete;
        },

        errorsPreventDelete (){
            //In case of errors this field can be removed only if one of them is in this field
            let preventDelete = this.modelsHasErrors;
            if(this.fieldInModelHasError({modelUid: this.currentModel.uid, field: this.property.name})){
                preventDelete = false;
            }
            return preventDelete;
        },
        propertyUpdated (){
            if(isEqual(JSON.parse(JSON.stringify(this.property)), JSON.parse(JSON.stringify(this.propertyBeforeUpdate)))){
                return false;
            }else{
                return true;
            }
        },
        currentLocale(){
            return this.$store.state.currentLocaleValue;
        },
        formIsValid(){
            if(this.$refs.form){
                return this.$refs.form.validate();
            }
        }
    },

    watch: {
        saveBtnError (val){
            if(val && this.property.name !== ''){
                this.$store.commit('ADD_MODEL_ERROR', {
                    modelUid: this.currentModel.uid,
                    modelName: this.currentModel.name,
                    provisionalFieldUid: this.property.provisionalFieldUid,
                });
                this.validateForm();
            }else{
                this.$store.commit('REMOVE_MODEL_ERROR', {
                    modelUid: this.currentModel.uid,
                    provisionalFieldUid: this.property.provisionalFieldUid,
                });
            }
        },
        'property.datatype' (val){
            // on change datatype reset attributes
            // if(oldVal !== val){
            //     this.property.setAttributes();
            // }
            if (val === 'm2m' || val === 'fk') { // || val === 'o2o'
                this.setDefaultReverseName();
            }
        },
        property: {
            handler (val, oldVal){
                //close suggestion modal when changing field without closing property form
                this.$refs['modal-case-suggestion'].close();
                if(oldVal === undefined){
                    this.$set(this, 'propertyBeforeUpdate', JSON.parse(JSON.stringify(this.property)));
                }
                this.validateForm();
                if(!this.saveBtnError){
                    this.$store.commit('SET_SAVE_STATE', this.propertyUpdated
                        ?'unsaved'
                        :'upToDate'
                    );
                }

                if(!val.hasOwnProperty('attributes') || !val.attributes){
                    val.setAttributes();
                }
                if(this.currentModel.representation_field === val.name){
                    this.representationField = true;
                }else{
                    this.representationField = false;
                }



                if(this.propertyOldValue !== null && val.provisionalFieldUid === this.propertyOldValue.provisionalFieldUid){
                    var attributeToDiff = diff(
                        val.attributes.to,
                        this.propertyOldValue.attributes.to,
                    );
                    if(attributeToDiff){
                        this.schema.updateFieldRelTarget(val, this.propertyOldValue.attributes.to);
                    }
                    if(val.name !== this.propertyOldValue.name){
                        this.schema.updateFieldRelName(this.property, this.propertyOldValue.name);
                        this.schema.updateQueriesFieldName(this.property, this.propertyOldValue.name);
                        this.$refs['modal-case-suggestion'].close();

                    }
                }
                this.$set(this, 'propertyOldValue', JSON.parse(JSON.stringify(val)));
                this.validateForm();
            },
            deep: true,
            immediate: true,
        },
        formIsValid (val){
            if(val){
                this.triedSavedWithError = false;
            }
        },
        currentLocale(){
            this.$refs['modal-case-suggestion'].msg = this.invalidNameCase.error;
        },
    },
    mounted(){
    },
    methods: {
        setIpProtocol (protocol){
            this.$set(this.property.attributes, 'protocol', protocol);
        },
        setDefaultReverseName (){
            this.property.attributes.reverse = snakeCase(`${this.currentModel.name}s`);
        },
        checkForSuggestion (){
            this.validateForm();
            if(this.invalidNameCase){
                this.$refs['modal-case-suggestion'].open(this.invalidNameCase, {top:'150px',left:'0'});
            }
        },
        onDatatypeChange (value){
            // obliger de mettre ce settimeout sinon le trigger agissait sur le composant déja monter (car on à des composants différents pour les datafieldType)
            setTimeout(() => {
                if(this.$refs['field-type']){
                    this.$refs['field-type'].resetDefaultValue();
                }
            },100);
        },
        onCloseProperty (){
            if(!this.property.name || !this.property.datatype || this.errorsPreventDelete) {
                this.$refs['modal-delete-field-on-close'].open();
            }else{
                this.$eventBus.$emit('save-schema');
                this.closeProperty();
            }
        },
        closeProperty (){
            if(!this.property.name || !this.property.datatype || this.errorsPreventDelete) {
                this.deleteProperty();
            }
            this.$emit('abort-property-edition');
        },
        onDeleteProperty (){
            if(!this.readonly){
                this.$refs['modal-delete-field'].open();
            }
        },
        deleteProperty (){
            this.$emit('abort-property-edition');
            if(this.modelsHasErrors){
                this.$store.commit('REMOVE_MODEL_ERROR', {
                    modelUid: this.currentModel.uid,
                    provisionalFieldUid: this.property.provisionalFieldUid,
                });
            }
            this.schema.deleteField(this.property);
            this.$store.dispatch('updateEditorSaveBtnIfDataModelEdited', this.schema);
            this.$eventBus.$emit('save-schema');
        },
        updateRepresentationField (val){
            if(val === true){
                this.currentModel.setRepresentationField(this.property.name);
            }else{
                this.currentModel.setRepresentationField('');
            }
        },

        saveModelisation (){
            if(!this.saveBtnError && !this.modelsHasErrors){
                if(this.property.datatype === 'bool'){
                    if(typeof this.property.attributes.default !== 'boolean'){
                        // valeur par défaut si non renseigné -> false
                        if(this.property.attributes.default !== undefined && this.property.attributes.default.toLowerCase() === 'true'){
                            this.property.attributes.default = true;
                        }else{
                            this.property.attributes.default = false;
                        }
                    }
                }
                if(this.property.attributes.unique === false){
                    delete this.property.attributes.unique;
                }
                this.$eventBus.$emit('save-schema');
                this.closeProperty();
                this.triedSavedWithError = false;
            }else{
                this.validateForm();
                this.triedSavedWithError = true;
            }
        },

        searchForMatchingReverseName (reverseName){
            let allModelRelFields = this.schema.allModelRelFields.filter((field) => {
                return field.provisionalFieldUid !== this.property.provisionalFieldUid;
            });
            if(this.property.attributes.to === undefined) return false;
            for(let field of allModelRelFields){
                if(
                   field.attributes.to.uid === this.property.attributes.to.uid &&
                   field.attributes.reverse === reverseName
                ){
                    return field;
                }
            }
            return false;
        },
        validateForm (){
            if(this.$refs.form){
                this.$refs.form.validate();
            }
        },
        onInputError (message){
            if(!this.fieldHasError){
                this.fieldHasError = true;
                this.$emit('updateErrorField', this.property.name, true);
            }
            return message;
        },
        onInputValid (val){
            if(val){
                this.fieldHasError = false;
                this.$emit('updateErrorField', this.property.name, false);
            }
            return val;
        },
    },
};
</script>

<style lang="scss">
.propertie-checkbox, .propertie-checkbox.v-input--selection-controls:not(.v-input--hide-details) .v-input__slot{
    margin: 0;
}
.property-actions-container{
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .update-buton{
        margin-bottom: 1rem;
    }
}
.property-close-btn{
    right: 0;
}
.representation-field-container {
    display: flex;
    align-items: center;
}
.property-name-suggestion-modal{
    position: absolute;
    right : 0 !important;
    bottom: 0 !important;
    height: 300px;
    width: 100%;
}
.grow-enter-active{
    animation: bounce-in .5s;
}
@keyframes bounce-in {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/page/Instance/DataModel/FormEditor/propertie-form.vue